import { useTranslation } from "react-i18next";
import { Button } from "@tellescope/design-system";
import { cn } from "@tellescope/ui";
import { useAddToCart } from "../useAddToCart";
import { formatShopPrice } from "../formatShopPrice";
import { getDefaultVariant } from "../shopVariants";
import { getReadableLearnMoreColor } from "../getReadableLearnMoreColor";
import { PriceBadge } from "../atoms/PriceBadge";
import { SecondaryProductBadge } from "../atoms/SecondaryProductBadge";
import { ProductImageCarousel } from "./ProductImageCarousel";
import type { ShopProduct } from "../types";

interface ProductCardProps {
  product: ShopProduct;
  onLearnMore?: (product: ShopProduct) => void;
  /** Optional second badge shown next to the price — e.g. "Recommended for you" or "Popular". */
  secondaryBadge?: string;
  /** Number of lines the description clamps to before truncating with an ellipsis. Defaults to 1 (main grid); the recommended rail uses 2. */
  descriptionLines?: 1 | 2;
  className?: string;
  /** The org's configured primary theme color (hex), if any — resolved by the caller from portal theming settings. Determines whether "Learn more" text uses --primary or --primary-foreground to stay readable. */
  primaryColorHex?: string;
}

export const ProductCard = ({
  product,
  onLearnMore,
  secondaryBadge,
  descriptionLines = 1,
  className,
  primaryColorHex,
}: ProductCardProps) => {
  const { t } = useTranslation("shop");
  const { addToCart } = useAddToCart(product);
  const learnMoreColor = getReadableLearnMoreColor(primaryColorHex);
  const defaultVariant = getDefaultVariant(product);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onLearnMore?.(product)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onLearnMore?.(product);
        }
      }}
      className={cn(
        "rounded-[24px] overflow-hidden border border-border bg-card shadow-sm flex flex-col cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        className
      )}
    >
      <div className="relative flex-shrink-0">
        <ProductImageCarousel name={product.title} images={product.image ? [product.image] : []} />
        {secondaryBadge && (
          <SecondaryProductBadge label={secondaryBadge} className="absolute top-3 left-3 z-10 text-xs px-2.5 py-1" />
        )}
        <PriceBadge
          label={formatShopPrice(t, defaultVariant)}
          className="absolute top-3 right-3 z-10 text-xs px-2.5 py-1"
        />
      </div>

      <div
        className="p-5 flex flex-col gap-2 flex-1"
        style={{ background: "linear-gradient(135deg, hsl(var(--primary) / 0.1) 0%, hsl(var(--primary) / 0) 100%)" }}
      >
        <h3 className="font-semibold text-foreground text-base leading-tight">{product.title}</h3>
        {product.description && (
          <p
            className={cn(
              "text-xs text-muted-foreground leading-relaxed",
              descriptionLines === 1 ? "line-clamp-1" : "line-clamp-2"
            )}
          >
            {product.description}
          </p>
        )}
        <div className="flex items-center justify-between mt-auto pt-3">
          <Button
            variant="link"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              onLearnMore?.(product);
            }}
            style={{ color: learnMoreColor }}
            className="h-auto p-0 font-medium"
          >
            {t("Learn more")}
          </Button>
          <Button
            data-test="add-to-cart"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              addToCart();
            }}
          >
            {t("Add to cart")}
          </Button>
        </div>
      </div>
    </div>
  );
};
